import { resolveApiUrl } from "../../utils/url/urlResolver";
import type { MediaItem } from "../types/mediaLibraryTypes";
import { mediaTransformer } from "./mediaTransformer";

export type MediaAPIParams = {
  q?: string;
  page?: number;
  mediaTypes?: string[];
  productPortfolios?: string[];
  languages?: string[];
  segments?: string[];
  fetchFavourite?: boolean;
  sortOrder?: "asc" | "desc";
};

export function generateDataFromUrl(): MediaAPIParams {
  const params = new URLSearchParams(window.location.search);

  return {
    q: params.get("q") ?? "",
    mediaTypes: params.get("mediaTypes")?.split(",") ?? [],
    productPortfolios: params.get("productPortfolios")?.split(",") ?? [],
    languages: params.get("languages")?.split(",") ?? [],
    segments: params.get("segments")?.split(",") ?? [],
    fetchFavourite: params.get("fetchFavourite") === "true" ? true : false,
    sortOrder: params.get("asc") === "false" ? "desc" : "asc",
  };
}

export function generateUrl(apiParams: MediaAPIParams) {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  const { q, mediaTypes, productPortfolios, languages, segments, fetchFavourite, sortOrder } = apiParams;

  params.set("q", q ?? "");

  const setListParam = (key: string, list?: string[] | null, keepEmpty = false) => {
    if (list?.length) {
      params.set(key, list.join(","));
    } else {
      if (keepEmpty) {
        params.set(key, "");
      } else {
        params.delete(key);
      }
    }
  };
  setListParam("mediaTypes", mediaTypes);
  setListParam("productPortfolios", productPortfolios);
  setListParam("selectedLanguages", languages, true); // to handle user deselected the language from shop language preselection
  setListParam("segments", segments);

  if (fetchFavourite !== undefined) {
    params.set("fetchFavourite", String(fetchFavourite));
  }
  if (sortOrder !== undefined) {
    params.set("asc", String(sortOrder === "asc"));
  }

  return url.toString();
}

export function getSearchEndpointParams(apiParams: MediaAPIParams): URLSearchParams {
  const { q, page = 0, mediaTypes, productPortfolios, languages, segments, fetchFavourite, sortOrder } = apiParams;
  const searchParams = new URLSearchParams();

  if (q) {
    searchParams.set("q", q);
  }
  if (page !== undefined) {
    searchParams.set("page", String(page));
  }
  if (mediaTypes?.length) {
    searchParams.set("mediaTypes", mediaTypes.join(","));
  }
  if (productPortfolios?.length) {
    searchParams.set("productPortfolios", productPortfolios.join(","));
  }
  if (languages?.length) {
    searchParams.set("languages", languages.join(","));
  }
  if (segments?.length) {
    searchParams.set("segments", segments.join(","));
  }
  if (fetchFavourite !== undefined) {
    searchParams.set("fetchFavourite", String(fetchFavourite));
  }
  if (sortOrder !== undefined) {
    searchParams.set("asc", String(sortOrder === "asc"));
  }

  return searchParams;
}

export async function fetchMediaAssets(params: MediaAPIParams): Promise<MediaItem[]> {
  window.history.replaceState({}, "", generateUrl(params));

  const searchParams = getSearchEndpointParams(params);

  const url = `/media-library/search?${searchParams}`;
  const apiUrl = resolveApiUrl(url);

  const response = await fetch(apiUrl, {
    method: "GET",
    credentials: "same-origin",
    mode: "cors",
    referrer: "no-referrer",
    headers: {
      dataType: "json",
      "content-type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Error status: ${response.status}`);
  }

  const data = await response.json();
  return mediaTransformer(data);
}
